import { FINAL_TRAINING_STATUSES } from '@/features/trainings/constants';
import { distillation_in_progress } from '@/shared/constants/taskStatuses';

/** `distillation_status` values on the evaluation response that mean "nothing is running". */
const TERMINAL_DISTILLATION_STATUSES = new Set(['completed', 'failed', 'canceled', 'cancelled']);

const isNonFinalTrainingStatus = (status) =>
    typeof status === 'string' && !FINAL_TRAINING_STATUSES.includes(status.toLowerCase());

/**
 * Whether a training is still running for the usecase whose Evaluations tab
 * has no versions yet (so the tab should show "Crunching the numbers" instead
 * of the plain "An overview will appear here" empty state).
 *
 * The no-versions evaluation response carries no in-progress flags
 * (`_create_empty_response` resets them), so the realtime `trainStatuses`
 * map and the usecase status are the primary signals; the response flags are
 * still honoured for the cases where the backend does populate them.
 *
 * @param {Object} params
 * @param {Object} [params.trainStatuses] Global realtime map keyed by distillation_event_id.
 * @param {string} [params.usecaseId] Current (llm) usecase id.
 * @param {string} [params.usecaseStatus] Current usecase status label.
 * @param {string} [params.distillationEventId] Known distillation event for this evaluation.
 * @param {Object} [params.evaluationResult] Evaluation API response.
 * @returns {boolean}
 */
export function isTrainingInProgress({
    trainStatuses,
    usecaseId,
    usecaseStatus,
    distillationEventId,
    evaluationResult,
} = {}) {
    const statuses = trainStatuses && typeof trainStatuses === 'object' ? trainStatuses : {};

    if (distillationEventId && isNonFinalTrainingStatus(statuses[distillationEventId]?.status)) {
        return true;
    }

    if (usecaseId) {
        const ownsUsecase = (entry) =>
            entry && (entry.usecase_id === usecaseId || entry.llm_usecase_id === usecaseId);
        if (Object.values(statuses).some((entry) => ownsUsecase(entry) && isNonFinalTrainingStatus(entry.status))) {
            return true;
        }
    }

    if (usecaseStatus === distillation_in_progress) {
        return true;
    }

    if (evaluationResult?.student_model_processing || evaluationResult?.has_incomplete_trainings) {
        return true;
    }

    const distillationStatus = evaluationResult?.distillation_status;
    return (
        typeof distillationStatus === 'string' &&
        distillationStatus.length > 0 &&
        !TERMINAL_DISTILLATION_STATUSES.has(distillationStatus.toLowerCase())
    );
}
